import { token } from '@/infrastructure/container';
import type { ProjectEvent, ProjectState } from '@/domain/project';
import type { SessionEvent } from '@/domain/session';

// The two access facades, pinned by session/contracts/p2-access.md. The real implementations sit
// over @celeriant/sdk (redeemShare, the online handle, handle.share); tests hand the stores an
// in-memory one.
//
// Two facades and not one on purpose. A device that scanned a QR gets IAccessGateway, which can
// redeem and open and nothing else. Minting needs Owner on the aggregate and only the admin laptop
// has it (docs/sync.md), so it lives on a separate interface that no onboarding screen is handed.

// STUB. P2's implementation fills these in. The interfaces are the frozen part.

export interface IAccessGateway {
  // Redeem BEFORE open, always: opening an online handle subscribes to realtime immediately and
  // the realtime endpoint refuses an ungranted client (docs/access.md, "Redemption ordering").
  redeemProjectShare(projectId: string, shareKey: string): Promise<void>;
  redeemSessionShare(sessionId: string, shareKey: string): Promise<void>;
  // Opens the handle and starts realtime.
  openProject(projectId: string): Promise<void>;
  openSession(sessionId: string): Promise<void>;
  // MobX observable. Valid after openProject; the share registry is read out of here.
  readonly projectState: ProjectState;
}

export const TOKEN_IAccessGateway = token<IAccessGateway>('IAccessGateway');

export interface IAdminGateway {
  // ONE write attempt on the project aggregate. Rejects with ConflictError on an OCC clash,
  // ServerError otherwise. The retry ladder belongs to the caller, not to this method.
  executeProject(event: ProjectEvent, opts: { idempotencyKey: string }): Promise<void>;
  // The creating write on a fresh session aggregate (the initial thresholdSet), and every write
  // after it. Creating the aggregate is what makes this device its owner and therefore the only
  // device that can mint its share codes (docs/sync.md, "Creating a session aggregate").
  executeSession(
    sessionId: string,
    event: SessionEvent,
    opts: { idempotencyKey: string },
  ): Promise<void>;
  // A contributor, multi-use share key on the session aggregate. Multi-use because three coaches
  // scan the same court code.
  mintSessionShare(sessionId: string): Promise<{ shareKey: string }>;
  // The one viewer, multi-use share key on the project aggregate, carried by every QR so that a
  // redeeming device can read the registry.
  mintProjectViewerShare(): Promise<{ shareKey: string }>;
  readonly projectState: ProjectState;
}

export const TOKEN_IAdminGateway = token<IAdminGateway>('IAdminGateway');
